import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { InteracoesTimeline } from '../../InteracoesTimeline';
import { Target, FileText, Edit, CheckCircle2, Ship, Gavel, ArrowRight } from 'lucide-react';
import { formatCurrency, formatDate } from '../crmUtils';

export const DetalhesDialog = ({ open, onOpenChange, oportunidade, origens, etapas, onNavigatePropostas, onNovaTarefa, onEdit, onRefresh }) => {
  if (!oportunidade) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-600" />
            {oportunidade.titulo}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Card><CardContent className="p-3 text-center"><div className="text-lg font-bold text-green-600">{formatCurrency(oportunidade.valor_estimado)}</div><div className="text-xs text-gray-500">Valor Estimado</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-lg font-bold text-blue-600">{oportunidade.probabilidade || 0}%</div><div className="text-xs text-gray-500">Probabilidade</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-lg font-bold text-purple-600">{formatCurrency((oportunidade.valor_estimado || 0) * (oportunidade.probabilidade || 0) / 100)}</div><div className="text-xs text-gray-500">Valor Ponderado</div></CardContent></Card>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Cliente:</span><span className="ml-2 font-medium">{oportunidade.cliente_nome || oportunidade.contato_nome || '-'}</span></div>
            <div><span className="text-gray-500">Responsável:</span><span className="ml-2 font-medium">{oportunidade.responsavel_nome || '-'}</span></div>
            <div><span className="text-gray-500">Origem:</span><span className="ml-2 font-medium">{origens.find(o => o.id === oportunidade.origem)?.nome || '-'}</span></div>
            <div><span className="text-gray-500">Previsão:</span><span className="ml-2 font-medium">{formatDate(oportunidade.data_previsao_fechamento)}</span></div>
          </div>

          {oportunidade.origem === 'licitacao' && oportunidade.licitacao_numero_processo && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-1"><Gavel className="h-4 w-4" />Dados da Licitação</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Processo:</span><span className="ml-2">{oportunidade.licitacao_numero_processo}</span></div>
                <div><span className="text-gray-500">Órgão:</span><span className="ml-2">{oportunidade.licitacao_orgao || '-'}</span></div>
                <div><span className="text-gray-500">Abertura:</span><span className="ml-2">{formatDate(oportunidade.licitacao_data_abertura)}</span></div>
                <div><span className="text-gray-500">Encerramento:</span><span className="ml-2">{formatDate(oportunidade.licitacao_data_encerramento)}</span></div>
              </div>
            </div>
          )}

          {oportunidade.origem === 'indicacao' && oportunidade.indicado_por_cliente_nome && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="text-sm font-medium text-blue-700 mb-1">Indicado por</h4>
              <p className="text-sm">{oportunidade.indicado_por_cliente_nome}</p>
            </div>
          )}

          {oportunidade.embarcacoes?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Embarcações ({oportunidade.embarcacoes.length})</h4>
              <div className="space-y-1">
                {oportunidade.embarcacoes.map((emb, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg p-2">
                    <Ship className="h-4 w-4 text-gray-400" />
                    <span>{emb.modelo_nome || 'Modelo não definido'}</span>
                    <Badge variant="secondary" className="text-xs">x{emb.quantidade}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <InteracoesTimeline
            oportunidadeId={oportunidade.id}
            interacoes={oportunidade.interacoes || []}
            onUpdate={onRefresh}
            onCriarTarefa={(titulo, dataVencimento) => {
              onNovaTarefa(oportunidade, titulo, dataVencimento);
            }}
          />

          {oportunidade.historico_etapas?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Histórico de Etapas</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {oportunidade.historico_etapas.slice().reverse().map((h, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-gray-500 border-l-2 border-gray-200 pl-2">
                    <span className="font-medium">{formatDate(h.data)}</span>
                    <ArrowRight className="h-3 w-3" />
                    <span>{etapas.find(e => e.id === h.etapa_nova)?.nome || h.etapa_nova}</span>
                    {h.observacao && <span className="text-gray-400">- {h.observacao}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-md" onClick={() => {
            onOpenChange(false);
            onNavigatePropostas(oportunidade.id);
          }}>
            <FileText className="h-4 w-4" />Criar Proposta
          </Button>
          <Button variant="outline" className="gap-1 text-amber-600 border-amber-200" onClick={() => onNovaTarefa(oportunidade)}>
            <CheckCircle2 className="h-4 w-4" />Nova Tarefa
          </Button>
          <Button variant="outline" onClick={() => { onOpenChange(false); onEdit(oportunidade); }}>
            <Edit className="mr-2 h-4 w-4" />Editar
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
